'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import BackgroundRenderer from './backgrounds/background-renderer';
import DevControls from './backgrounds/dev-controls';
import { backgrounds, getRandomProductionBackground } from './backgrounds/registry';
import { useMediaQuery } from '@/lib/hooks/use-media-query';

/** Tailwind's `lg` — the breakpoint at which the background panel is visible. */
const LG_BREAKPOINT = '(min-width: 1024px)';

const QUOTES: { text: string; author: string }[] = [
  // --- Real quotes: connections, networks, the web ---
  { text: 'The world is nothing but a great desire to live and a great dissatisfaction with living.', author: 'Federico García Lorca' },
  { text: 'The internet is becoming the town square for the global village of tomorrow.', author: 'Bill Gates' },
  { text: 'We are all now connected by the internet, like neurons in a giant brain.', author: 'Stephen Hawking' },
  { text: 'The web does not just connect machines, it connects people.', author: 'Tim Berners-Lee' },
  { text: 'Any sufficiently advanced technology is indistinguishable from magic.', author: 'Arthur C. Clarke' },
  { text: 'The best way to predict the future is to invent it.', author: 'Alan Kay' },
  { text: 'Simplicity is the ultimate sophistication.', author: 'Leonardo da Vinci' },
  { text: 'Make it work, make it right, make it fast.', author: 'Kent Beck' },
  { text: 'First, solve the problem. Then, write the code.', author: 'John Johnson' },
  { text: 'The details are not the details. They make the design.', author: 'Charles Eames' },
  { text: 'Good design is as little design as possible.', author: 'Dieter Rams' },
  { text: 'Information is the oil of the 21st century, and analytics is the combustion engine.', author: 'Peter Sondergaard' },

  // --- Product-aligned: routing, paths, speed, direction ---
  { text: 'The shortest distance between two points is a well-routed link.', author: 'rii.link' },
  { text: 'Every great journey begins with a single redirect.', author: 'rii.link' },
  { text: 'In a world of broken links, be the one that always resolves.', author: 'rii.link' },
  { text: 'Speed is not about moving fast. It\u2019s about removing friction.', author: 'rii.link' },
  { text: 'Behind every click is a decision. Make it count in milliseconds.', author: 'rii.link' },
  { text: 'Your links are your handshake with the internet.', author: 'rii.link' },
  { text: 'Route intelligently. Arrive instantly.', author: 'rii.link' },
  { text: 'The best infrastructure is the kind you never notice \u2014 until it\u2019s gone.', author: 'rii.link' },
  { text: 'A link is a promise. We make sure it\u2019s kept.', author: 'rii.link' },
  { text: 'Don\u2019t just shorten links. Make them smarter.', author: 'rii.link' },

  // --- Witty / developer humor ---
  { text: 'There are only two hard things in CS: cache invalidation, naming things, and off-by-one errors.', author: 'Leon Bambrick' },
  { text: '404 is not just an error. It\u2019s a missed opportunity.', author: 'rii.link' },
  { text: 'A URL walks into a bar. The bartender says: \u201CWe don\u2019t serve your type here.\u201D 301 to the next bar.', author: 'rii.link' },
  { text: 'I told my links to get their act together. Now they all redirect properly.', author: 'rii.link' },
];

function getRandomQuote() {
  return QUOTES[Math.floor(Math.random() * QUOTES.length)];
}

export default function AuthLayout({ children }: { children: React.ReactNode }) {
  const [activeId, setActiveId] = useState(backgrounds[0].id);
  const [activeProps, setActiveProps] = useState<Record<string, unknown>>({ ...backgrounds[0].brandDefaults });
  const [quote, setQuote] = useState(QUOTES[0]);

  useEffect(() => {
    // Randomization must happen after hydration: the server-rendered markup
    // has to match the first client render, so this cannot move into the
    // useState initializers.
    /* eslint-disable react-hooks/set-state-in-effect */
    const bg = getRandomProductionBackground();
    setActiveId(bg.id);
    setActiveProps({ ...bg.brandDefaults });
    setQuote(getRandomQuote());
    /* eslint-enable react-hooks/set-state-in-effect */
  }, []);

  const activeEntry = backgrounds.find((b) => b.id === activeId) || backgrounds[0];

  // The panel below is `hidden lg:flex`, but CSS `display: none` still mounts
  // the subtree — so without this gate every phone boots a WebGL renderer for a
  // panel it never shows. Matching the breakpoint in JS skips the mount instead.
  const showBackground = useMediaQuery(LG_BREAKPOINT);

  return (
    <div className="flex min-h-screen">
      {/* Left: Background + quote (hidden < lg) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-black">
        {showBackground && (
          <BackgroundRenderer
            backgroundId={activeId}
            props={activeProps}
            requiresWebGL={activeEntry.requiresWebGL}
          />
        )}

        {/* Quote overlay */}
        <div className="absolute bottom-12 left-12 right-12 z-10 pointer-events-none">
          <blockquote className="text-2xl font-light text-white/90 leading-relaxed drop-shadow-lg">
            &ldquo;{quote.text}&rdquo;
          </blockquote>
          <p className="mt-4 text-sm text-white/50">&mdash; {quote.author}</p>
        </div>

        {/* Dev controls (development only) */}
        {process.env.NODE_ENV === 'development' && (
          <DevControls
            activeEntry={activeEntry}
            activeProps={activeProps}
            onChangeBackground={setActiveId}
            onChangeProps={setActiveProps}
          />
        )}
      </div>

      {/* Right: Form content */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="flex justify-center">
            <Image
              src="/logos/logo-dark.png"
              alt="rii.link"
              width={150}
              height={40}
              className="dark:hidden"
              priority
            />
            <Image
              src="/logos/logo-light.png"
              alt="rii.link"
              width={150}
              height={40}
              className="hidden dark:block"
              priority
            />
          </div>

          {children}
        </div>
      </div>
    </div>
  );
}
